import { createContext, useEffect, useState } from 'react';
import authConfig from 'config/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import usePathname from 'hooks/custom/usePathname';
import { loginUser } from '@fake-db/auth/jwt';
import { Outlet } from 'react-router-dom';
import { createMongoAbility } from '@casl/ability';
import { defineAbilityFor } from './ability';
// import userPermission from './localUserPermissions';
import { getItemFromStore } from 'helpers/utils';
import fetchData from 'services/fetchData';
import apiEndpoints from 'constants/apiEndpoints';

const sleep = time => new Promise(res => setTimeout(res, time));

const defaultUser = {
  user: null,
  abilities: createMongoAbility(),
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  handleLoginPage: () => null,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve()
};

const AuthContext = createContext(defaultUser);

// Expose a method to handle modal opening
// window.openModalFromFlutter = () => {
//   // Code to open a modal
//   alert('Modal opened by window.openModalFromFlutter');
//   console.log('Modal opened by window.openModalFromFlutter');
// };

class CustomError extends Error {
  constructor(message, status) {
    super(message); // Pass message to the base Error class
    this.status = status;
  }
}

const AuthProvider = () => {
  const [user, setUser] = useState(defaultUser.user);
  const [loading, setLoading] = useState(defaultUser.loading);
  const [abilities, setAbilities] = useState(defaultUser.abilities); // CASL abilities
  const pathname = usePathname();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = window.localStorage.getItem(
        authConfig.storageTokenKeyName
      );
      if (storedToken) {
        setLoading(true);
        const userDataString = window.localStorage.getItem('kerpUserData2.1');
        if (userDataString) {
          const userData = JSON.parse(userDataString || '{}');
          const loginTime = new Date(userData.loginTime);
          const currentTime = new Date();
          const isInMobileApp = getItemFromStore('loginInMobileApp', false);
          const fetchPermissionResponse = await fetchData(
            apiEndpoints.rolesPermission.getPermission(userData.role_id)
          );

          const permissionData = fetchPermissionResponse.status
            ? JSON.parse(fetchPermissionResponse.data.permission_json)
            : null;

          if (currentTime - loginTime < 1 * 30 * 60 * 1000 || isInMobileApp) {
            userData.loginTime = currentTime;
            window.localStorage.setItem(
              'kerpUserData2.1',
              JSON.stringify(userData)
            );
            window.localStorage.setItem('krpLogin', true);
            setUser({ ...userData });

            const userRole = userData.user_role;
            // const userPermission = JSON.parse(userData.permission_json || '');
            setAbilities(defineAbilityFor(userRole, permissionData || null));
          } else {
            window.localStorage.setItem('kerpUserData2.1', '');
            window.localStorage.setItem(authConfig.storageTokenKeyName, null);
            window.localStorage.setItem('krpLogin', false);
            setUser(null);
            if (
              !pathname.includes('/auth/login') &&
              !pathname.includes('/auth/autoLogin')
            ) {
              navigate('/auth/login', {
                state: { returnUrl: location.pathname },
                replace: true
              });
            }
          }
        } else {
          if (!pathname.includes('/auth/login')) {
            navigate('/auth/login', {
              state: { returnUrl: location.pathname },
              replace: true
            });
          }
        }
        setLoading(false);
      } else {
        // localStorage.clear();

        window.localStorage.setItem('kerpUserData2.1', '');
        window.localStorage.setItem(authConfig.storageTokenKeyName, null);
        window.localStorage.setItem('krpLogin', false);
        setUser(null);
        if (
          !pathname.includes('/auth/login') &&
          !pathname.includes('/auth/autoLogin')
        ) {
          navigate('/auth/login', {
            state: { returnUrl: location.pathname },
            replace: true
          });
        }
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const handleLogin = async (params, setLoading, errorCallback) => {
    await sleep(1000);
    try {
      const [status, response] = await loginUser(
        params.mobile,
        params.password
      );
      if (status === 200) {
        const loginTime = new Date();
        const userRole = response.userData.user_role;
        const userPermission = JSON.parse(
          response.userData.permission_json || ''
        );
        setAbilities(defineAbilityFor(userRole, userPermission || null));

        if (params.rememberMe) {
          window.localStorage.setItem(
            authConfig.storageTokenKeyName,
            response.accessToken
          );

          window.localStorage.setItem(
            'kerpUserData2.1',
            JSON.stringify({ ...response.userData, loginTime: loginTime })
          );
          window.localStorage.setItem('loginInMobileApp', false);

          // JSON.stringify({ ...response.userData, loginTime })
          window.localStorage.setItem('krpLogin', true);
        }
        const returnUrl = location.state?.returnUrl || '/';
        setUser({ ...response.userData, userRole: 'HOD' });
        setLoading(false);
        const redirectURL = returnUrl;
        navigate(redirectURL, { replace: true });
      } else if (status === 403) {
        // Handle permission error
        setLoading(false);
        throw new CustomError(
          'You do not have permission to access this panel.',
          403
        );
      } else {
        setLoading(false);
        throw new CustomError(response.error || 'Login failed', status);
      }
    } catch (error) {
      setLoading(false);
      if (errorCallback) errorCallback(error, error.status);
    }
  };

  const handleLogout = () => {
    setUser(null);
    window.localStorage.setItem('kerpUserData2.1', '');
    window.localStorage.setItem(authConfig.storageTokenKeyName, null);
    window.localStorage.setItem('krpLogin', false);
    navigate('/auth/login');
  };

  const handleLoginPage = () => {
    navigate('/auth/login', {
      search: '?returnUrl=' + encodeURIComponent(window.location.pathname)
    });
  };

  const values = {
    user,
    abilities,
    loading,
    setUser,
    setLoading,
    handleLoginPage,
    login: handleLogin,
    logout: handleLogout
  };

  return (
    <AuthContext.Provider value={values}>
      <Outlet />
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
